<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm"
    @click.self="closeModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-5xl transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-visible">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
        <h2 class="text-lg font-bold m-0">Edit And Detail</h2>
        <button type="button" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white" @click="closeModal">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form class="p-6 sm:p-8 relative" @submit.prevent="handleSubmit">
        <div
          v-if="formDataLoading"
          class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-b-xl"
        >
          <div class="h-8 w-8 border-4 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <p v-if="formDataError" class="mb-3 text-xs text-red-600">{{ formDataError }}</p>
        <div class="mb-4">
          <div class="inline-flex items-center gap-2">
            <span class="text-[0.75rem] font-bold capitalize text-white bg-emerald-600 px-2 py-1 rounded">Employee ID No.</span>
            <input
              v-model="form.emp_idno"
              type="text"
              readonly
              class="px-3 py-1.5 border border-[#cbd5e1] rounded-md outline-none text-sm bg-slate-50 text-slate-600 w-28"
            >
          </div>
        </div>

        <div class="mb-2">
          <span class="text-[0.75rem] font-bold capitalize text-[#0d4169] bg-slate-100 px-2 py-1 rounded">Personal Information</span>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-3 mb-5">
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Full Name</label>
            <input
              v-model="form.full_name"
              type="text"
              placeholder="Full name"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            >
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Mothers Name</label>
            <input
              v-model="form.mother_name"
              type="text"
              placeholder="Mother's name"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            >
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Sex</label>
            <select
              v-model="form.sex"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white cursor-pointer"
            >
              <option value="" disabled>Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Birth Date</label>
            <input
              v-model="form.birth_date"
              type="date"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            >
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Phone No.</label>
            <input
              v-model="form.phone_number"
              type="text"
              maxlength="10"
              placeholder="09xxxxxxxx"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            >
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">E-Mail</label>
            <input
              v-model="form.email"
              type="email"
              placeholder="Email"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            >
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Region</label>
            <select
              v-model="form.region"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white cursor-pointer"
              @change="onPersonalRegionChange"
            >
              <option value="">Select region</option>
              <option
                v-for="regionName in regionOptions"
                :key="`region-${regionName}`"
                :value="regionName"
              >
                {{ regionName }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">City</label>
            <select
              v-model="form.city"
              :disabled="!form.region"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white cursor-pointer disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
              @change="onPersonalCityChange"
            >
              <option value="">{{ form.region ? 'Select city' : 'Select region first' }}</option>
              <option
                v-for="cityName in personalCityOptions"
                :key="`city-${cityName}`"
                :value="cityName"
              >
                {{ cityName }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Sub City</label>
            <select
              v-model="form.subcity"
              :disabled="!form.region || !form.city"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white cursor-pointer disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
            >
              <option value="">{{ form.city ? 'Select sub city' : 'Select city first' }}</option>
              <option
                v-for="subCityName in personalSubCityOptions"
                :key="`subcity-${subCityName}`"
                :value="subCityName"
              >
                {{ subCityName }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Woreda</label>
            <input
              v-model="form.woreda"
              type="text"
              placeholder="Woreda"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            >
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Kebele</label>
            <input
              v-model="form.kebele"
              type="text"
              placeholder="Kebele"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            >
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">House No.</label>
            <input
              v-model="form.house_no"
              type="text"
              placeholder="House no."
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            >
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Photo</label>
            <label
              for="employee-photo-upload"
              class="flex items-center gap-3 bg-white border border-dashed border-[#cbd5e1] rounded-lg p-2 cursor-pointer hover:border-[#2563eb] transition-all group min-h-[50px]"
              :class="{ 'border-[#2563eb] bg-blue-50/30': uploadingPhoto }"
            >
              <div v-if="form.photo" class="relative shrink-0">
                <img :src="resolveCompanyLogoUrl(form.photo)" class="w-10 h-10 object-contain rounded bg-slate-50 border border-slate-100">
                <button
                  type="button"
                  class="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] shadow-sm hover:bg-red-600 transition-colors z-10"
                  @click.prevent="form.photo = ''"
                >✕</button>
              </div>
              <div
                v-else
                class="w-10 h-10 rounded bg-slate-50 border border-dashed border-slate-300 flex items-center justify-center text-slate-300 group-hover:text-[#2563eb] group-hover:border-[#2563eb] transition-all shrink-0"
              >
                <svg v-if="!uploadingPhoto" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                <svg v-else class="w-5 h-5 animate-spin text-[#2563eb]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              </div>
              <div class="flex-1 leading-tight overflow-hidden">
                <p class="text-[0.75rem] font-bold text-[#0d4169] group-hover:underline truncate">
                  <span v-if="uploadingPhoto">Uploading...</span>
                  <span v-else>{{ form.photo ? 'Change Photo' : 'Choose Employee Photo' }}</span>
                </p>
                <p class="text-[0.65rem] text-slate-400 mt-0.5">PNG, JPG, max 3MB</p>
              </div>
              <input id="employee-photo-upload" type="file" class="hidden" accept="image/*" @change="handlePhotoUpload">
            </label>
          </div>
        </div>

        <div class="mb-2">
          <span class="text-[0.75rem] font-bold capitalize text-[#0d4169] bg-slate-100 px-2 py-1 rounded">Emergency Contact</span>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-3 mb-5">
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Contact Person Name</label>
            <input
              v-model="form.contact_name"
              type="text"
              placeholder="Contact name"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            >
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Contact Person Phone No.</label>
            <input
              v-model="form.contact_phone"
              type="text"
              maxlength="10"
              placeholder="09xxxxxxxx"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            >
          </div>
          <div class="flex flex-col gap-1.5 sm:col-span-2 md:col-span-1">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Contact Person Address</label>
            <input
              v-model="form.contact_address"
              type="text"
              placeholder="Address"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            >
          </div>
        </div>

        <div class="mb-2">
          <span class="text-[0.75rem] font-bold capitalize text-[#0d4169] bg-slate-100 px-2 py-1 rounded">Employment Type</span>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-3 mb-5">
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Employment Type</label>
            <select
              v-model="form.employment_type"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white cursor-pointer"
            >
              <option value="" disabled>Select</option>
              <option value="Permanent">Permanent</option>
              <option value="Contract">Contract</option>
              <option value="Part Time">Part Time</option>
              <option value="Commission">Commission</option>
            </select>
          </div>
          <div class="flex min-w-0 sm:col-span-2 md:col-span-2 lg:col-span-2">
            <div class="flex min-w-0 flex-1 flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Basic Salary</label>
              <input
                :value="form.basic_salary"
                type="number"
                step="0.01"
                placeholder="0.00"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
                @input="updateBasicSalary"
              >
            </div>
            <div class="flex min-w-0 flex-1 flex-col gap-1.5 border-l border-[#cbd5e1]">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Net Salary</label>
              <input
                :value="netSalary"
                type="number"
                step="0.01"
                placeholder="0.00"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
                @input="updateNetSalary"
              >
            </div>
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Working / Shift Time</label>
            <select
              v-model="form.shift_time"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white cursor-pointer"
            >
              <option value="" disabled>Select Here</option>
              <option
                v-for="shift in shiftTimes"
                :key="shift.id"
                :value="String(shift.id)"
              >
                {{ shift.shiftName }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Off Duty Day</label>
            <select
              v-model="form.off_duty_day"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white cursor-pointer"
            >
              <option value="" disabled>Select Here</option>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
              <option value="Saturday">Saturday</option>
              <option value="Sunday">Sunday</option>
              <option v-if="form.employment_type !== 'Permanent'" value="Once at Month">Once at Month</option>
              <option v-if="form.employment_type !== 'Permanent'" value="None">None</option>
              <option v-if="form.employment_type !== 'Permanent'" value="Other">Other</option>
            </select>
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Start Date</label>
            <input
              v-model="form.start_date"
              type="date"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            >
          </div>
        </div>

        <template v-if="showExtraSections">
          <div v-show="false">
            <div class="mb-2">
              <span class="text-[0.75rem] font-bold capitalize text-[#0d4169] bg-slate-100 px-2 py-1 rounded">Beneficiary / Allowance</span>
            </div>
            <div
              v-if="allowanceTypes.length"
              class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-3 mb-5"
            >
              <div
                v-for="item in allowanceTypes"
                :key="'allowance-' + item.id"
                class="flex flex-col gap-1.5"
              >
                <label class="text-[0.85rem] font-semibold text-[#1e293b]">{{ item.description }}</label>
                <input
                  v-model="allowanceValues[item.id]"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
                >
              </div>
            </div>
            <p
              v-else
              class="mb-5 text-sm text-slate-400 italic"
            >
              No allowance earn types defined for this company/branch.
            </p>
          </div>

          <div class="mb-2">
            <span class="text-[0.75rem] font-bold capitalize text-[#0d4169] bg-slate-100 px-2 py-1 rounded">Qualification</span>
          </div>
          <div class="mb-5 overflow-x-auto">
            <div class="min-w-[640px]">
              <div class="flex items-center gap-2 text-[0.8rem] font-semibold text-[#1e293b] mb-2">
                <div class="flex-1 min-w-0">Educational Level</div>
                <div class="flex-1 min-w-0">Field of Study</div>
                <div class="flex-1 min-w-0">College / University</div>
                <div class="flex-1 min-w-0">Graduated Year</div>
                <div class="w-10"></div>
              </div>
              <div
                v-for="(row, idx) in qualifications"
                :key="'qual-' + idx"
                class="flex items-center gap-2 mb-2"
              >
                <select
                  v-model="row.edu_level"
                  class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm flex-1 min-w-0 bg-white cursor-pointer"
                >
                  <option value="" disabled>Select level</option>
                  <option
                    v-for="level in educationLevels"
                    :key="level"
                    :value="level"
                  >
                    {{ level }}
                  </option>
                </select>
                <input
                  v-model="row.field"
                  type="text"
                  placeholder="Field"
                  class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm flex-1 min-w-0"
                >
                <input
                  v-model="row.college"
                  type="text"
                  placeholder="College"
                  class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm flex-1 min-w-0"
                >
                <input
                  v-model="row.graduated_year"
                  type="date"
                  class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm flex-1 min-w-0"
                >
                <div class="w-10 flex items-center justify-end">
                  <button
                    v-if="idx === qualifications.length - 1"
                    type="button"
                    class="h-6 w-6 flex items-center justify-center bg-green-500 text-white rounded-md text-xs font-bold"
                    @click="addQualification"
                  >+</button>
                  <button
                    v-else
                    type="button"
                    class="h-6 w-6 flex items-center justify-center bg-red-500 text-white rounded-md text-xs font-bold"
                    @click="removeQualification(idx)"
                  >-</button>
                </div>
              </div>
            </div>
          </div>

          <div class="mb-2">
            <span class="text-[0.75rem] font-bold capitalize text-[#0d4169] bg-slate-100 px-2 py-1 rounded">Experience</span>
          </div>
          <div class="mb-5 overflow-x-auto">
            <div class="min-w-[640px]">
              <div class="flex items-center gap-2 text-[0.8rem] font-semibold text-[#1e293b] mb-2">
                <div class="flex-1 min-w-0">FROM</div>
                <div class="flex-1 min-w-0">TO</div>
                <div class="flex-1 min-w-0">Position</div>
                <div class="flex-1 min-w-0">Company</div>
                <div class="w-10"></div>
              </div>
              <div
                v-for="(row, idx) in experiences"
                :key="'exp-' + idx"
                class="flex items-center gap-2 mb-2"
              >
                <input
                  v-model="row.from"
                  type="date"
                  class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm flex-1 min-w-0"
                >
                <input
                  v-model="row.to"
                  type="date"
                  class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm flex-1 min-w-0"
                >
                <input
                  v-model="row.position"
                  type="text"
                  placeholder="Position"
                  class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm flex-1 min-w-0"
                >
                <input
                  v-model="row.company"
                  type="text"
                  placeholder="Company"
                  class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm flex-1 min-w-0"
                >
                <div class="w-10 flex items-center justify-end">
                  <button
                    v-if="idx === experiences.length - 1"
                    type="button"
                    class="h-6 w-6 flex items-center justify-center bg-green-500 text-white rounded-md text-xs font-bold"
                    @click="addExperience"
                  >+</button>
                  <button
                    v-else
                    type="button"
                    class="h-6 w-6 flex items-center justify-center bg-red-500 text-white rounded-md text-xs font-bold"
                    @click="removeExperience(idx)"
                  >-</button>
                </div>
              </div>
            </div>
          </div>

          <div class="mb-2">
            <span class="text-[0.75rem] font-bold capitalize text-[#0d4169] bg-slate-100 px-2 py-1 rounded">Guarantee</span>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-3">
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Guarantee Date</label>
              <input
                v-model="form.g_date"
                type="date"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
              >
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Guarantee Name</label>
              <input
                v-model="form.g_name"
                type="text"
                placeholder="Name"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
              >
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Guarantee Region</label>
              <select
                v-model="form.g_region"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white cursor-pointer"
                @change="onGuaranteeRegionChange"
              >
                <option value="">Select region</option>
                <option
                  v-for="regionName in regionOptions"
                  :key="`g-region-${regionName}`"
                  :value="regionName"
                >
                  {{ regionName }}
                </option>
              </select>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Guarantee City</label>
              <select
                v-model="form.g_city"
                :disabled="!form.g_region"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white cursor-pointer disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
                @change="onGuaranteeCityChange"
              >
                <option value="">{{ form.g_region ? 'Select city' : 'Select region first' }}</option>
                <option
                  v-for="cityName in guaranteeCityOptions"
                  :key="`g-city-${cityName}`"
                  :value="cityName"
                >
                  {{ cityName }}
                </option>
              </select>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Guarantee Sub City</label>
              <select
                v-model="form.g_subcity"
                :disabled="!form.g_region || !form.g_city"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white cursor-pointer disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
              >
                <option value="">{{ form.g_city ? 'Select sub city' : 'Select city first' }}</option>
                <option
                  v-for="subCityName in guaranteeSubCityOptions"
                  :key="`g-subcity-${subCityName}`"
                  :value="subCityName"
                >
                  {{ subCityName }}
                </option>
              </select>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Guarantee Woreda</label>
              <input
                v-model="form.g_woreda"
                type="text"
                placeholder="Woreda"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
              >
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Guarantee Kebele</label>
              <input
                v-model="form.g_kebele"
                type="text"
                placeholder="Kebele"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
              >
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Guarantee House No.</label>
              <input
                v-model="form.g_houseno"
                type="text"
                placeholder="House no."
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
              >
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Guarantee Phone No.</label>
              <input
                v-model="form.g_phone"
                type="text"
                maxlength="10"
                placeholder="09xxxxxxxx"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
              >
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Guarantee Relationship</label>
              <select
                v-model="form.g_relation"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white cursor-pointer"
              >
                <option value="" disabled>Select</option>
                <option value="Family">Family</option>
                <option value="Friend">Friend</option>
                <option value="Partner">Partner</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Guarantee Job Type</label>
              <select
                v-model="form.g_jobtype"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white cursor-pointer"
              >
                <option value="" disabled>Select</option>
                <option value="Governmental">Governmental</option>
                <option value="Private">Private</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Guarantee Company</label>
              <input
                v-model="form.g_company"
                type="text"
                placeholder="Company"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
              >
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Monthly Salary</label>
              <input
                v-model="form.g_income"
                type="number"
                step="0.01"
                placeholder="0.00"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
              >
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Photo</label>
              <label
                for="guarantee-photo-upload"
                class="flex items-center gap-3 bg-white border border-dashed border-[#cbd5e1] rounded-lg p-2 cursor-pointer hover:border-[#2563eb] transition-all group min-h-[50px]"
                :class="{ 'border-[#2563eb] bg-blue-50/30': uploadingGuaranteePhoto }"
              >
                <div v-if="form.g_photo" class="relative shrink-0">
                  <img :src="resolveCompanyLogoUrl(form.g_photo)" class="w-10 h-10 object-contain rounded bg-slate-50 border border-slate-100">
                  <button
                    type="button"
                    class="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] shadow-sm hover:bg-red-600 transition-colors z-10"
                    @click.prevent="form.g_photo = ''"
                  >✕</button>
                </div>
                <div
                  v-else
                  class="w-10 h-10 rounded bg-slate-50 border border-dashed border-slate-300 flex items-center justify-center text-slate-300 group-hover:text-[#2563eb] group-hover:border-[#2563eb] transition-all shrink-0"
                >
                  <svg v-if="!uploadingGuaranteePhoto" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                  <svg v-else class="w-5 h-5 animate-spin text-[#2563eb]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                </div>
                <div class="flex-1 leading-tight overflow-hidden">
                  <p class="text-[0.75rem] font-bold text-[#0d4169] group-hover:underline truncate">
                    <span v-if="uploadingGuaranteePhoto">Uploading...</span>
                    <span v-else>{{ form.g_photo ? 'Change Photo' : 'Choose Guarantee Photo' }}</span>
                  </p>
                  <p class="text-[0.65rem] text-slate-400 mt-0.5">PNG, JPG, max 3MB</p>
                </div>
                <input id="guarantee-photo-upload" type="file" class="hidden" accept="image/*" @change="handleGuaranteePhotoUpload">
              </label>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Remark</label>
              <input
                v-model="form.g_remark"
                type="text"
                placeholder="Remark"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
              >
            </div>
          </div>
        </template>

        <div class="mt-8 pt-6 border-t border-slate-100 flex justify-end gap-2 items-center">
          <button
            type="button"
            class="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
            @click="closeModal"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="formDataLoading || saving"
            class="px-6 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:transform active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <div
              v-if="saving"
              class="h-4 w-4 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
            ></div>
            <span v-if="!saving">Save</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, reactive, ref, watch } from 'vue'
import { apiFetch, normalizeUploadValue, resolveCompanyLogoUrl } from '../../../utils/api'

const props = defineProps({
  isOpen: Boolean,
  employee: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['close', 'save'])

const educationLevels = [
  'Doctorate','PhD','Masters', 'Degree', 'Diploma', 'TVET','Certificate', 'High School', 'Under High School'
]

/** Nested sample: Region → City → Sub City */
const locationTree = [
  {
    region: 'Addis Ababa',
    cities: [
      {
        city: 'Addis Ababa',
        subCities: [
          'Addis Ketema',
          'Akaki Kality',
          'Arada',
          'Bole',
          'Gullele',
          'Kirkos',
          'Kolfe Keranio',
          'Lemi Kura',
          'Lideta',
          'Nifas Silk-Lafto',
          'Yeka',
        ],
      },
    ],
  },

  {
    region: 'Afar',
    cities: [
      { city: 'Abala', subCities: [] },
      { city: 'Asayita', subCities: [] },
      { city: 'Awash', subCities: [] },
      { city: 'Dubti', subCities: [] },
      { city: 'Logiya', subCities: [] },
      { city: 'Semera', subCities: [] },
    ],
  },

  {
    region: 'Amhara',
    cities: [
      { city: 'Bahir Dar', subCities: [] },
      { city: 'Bati', subCities: [] },
      { city: 'Chagne', subCities: [] },
      { city: 'Debre Birhan', subCities: [] },
      { city: 'Debre Markos', subCities: [] },
      { city: 'Debre Tabor', subCities: [] },
      { city: 'Dessie', subCities: [] },
      { city: 'Finote Selam', subCities: [] },
      { city: 'Gondar', subCities: [] },
      { city: 'Injibara', subCities: [] },
      { city: 'Kemise', subCities: [] },
      { city: 'Kombolcha', subCities: [] },
      { city: 'Lalibela', subCities: [] },
      { city: 'Motta', subCities: [] },
      { city: 'Woldiya', subCities: [] },
    ],
  },

  {
    region: 'Benishangul-Gumuz',
    cities: [
      { city: 'Asosa', subCities: [] },
      { city: 'Bambasi', subCities: [] },
      { city: 'Gilgel Beles', subCities: [] },
      { city: 'Kurmuk', subCities: [] },
      { city: 'Pawe', subCities: [] },
    ],
  },

  {
    region: 'Central Ethiopia',
    cities: [
      { city: 'Bonga', subCities: [] },
      { city: 'Butajira', subCities: [] },
      { city: 'Durame', subCities: [] },
      { city: 'Fonko', subCities: [] },
      { city: 'Halaba', subCities: [] },
      { city: 'Hossana', subCities: [] },
      { city: 'Tora', subCities: [] },
      { city: 'Welkite', subCities: [] },
      { city: 'Worabe', subCities: [] },
    ],
  },

  {
    region: 'Dire Dawa',
    cities: [
      {
        city: 'Dire Dawa',
        subCities: [
          'Addis Ketema',
          'Dechatu',
          'Gende Kore',
          'Legehar',
          'Sabian',
        ],
      },
    ],
  },

  {
    region: 'Gambela',
    cities: [
      { city: 'Abobo', subCities: [] },
      { city: 'Gambela', subCities: [] },
      { city: 'Itang', subCities: [] },
      { city: 'Pinyudo', subCities: [] },
    ],
  },

  {
    region: 'Harari',
    cities: [
      { city: 'Harar', subCities: [] },
    ],
  },

  {
    region: 'Oromia',
    cities: [
      { city: 'Adama', subCities: [] },
      { city: 'Ambo', subCities: [] },
      { city: 'Asella', subCities: [] },
      { city: 'Bako', subCities: [] },
      { city: 'Bale Robe', subCities: [] },
      { city: 'Bishoftu', subCities: [] },
      { city: 'Burayu', subCities: [] },
      { city: 'Dembi Dolo', subCities: [] },
      { city: 'Dukem', subCities: [] },
      { city: 'Gimbi', subCities: [] },
      { city: 'Holeta', subCities: [] },
      { city: 'Jimma', subCities: [] },
      { city: 'Meki', subCities: [] },
      { city: 'Mojo', subCities: [] },
      { city: 'Nekemte', subCities: [] },
      { city: 'Sebeta', subCities: [] },
      { city: 'Shambu', subCities: [] },
      { city: 'Shashamane', subCities: [] },
      { city: 'Woliso', subCities: [] },
      { city: 'Ziway', subCities: [] },
    ],
  },

  {
    region: 'Sidama',
    cities: [
      { city: 'Aleta Wendo', subCities: [] },
      { city: 'Bensa', subCities: [] },
      { city: 'Dilla', subCities: [] },
      { city: 'Hawassa', subCities: [] },
      { city: 'Wondo Genet', subCities: [] },
      { city: 'Yirgalem', subCities: [] },
    ],
  },

  {
    region: 'Somali',
    cities: [
      { city: 'Degehabur', subCities: [] },
      { city: 'Dollo', subCities: [] },
      { city: 'Fik', subCities: [] },
      { city: 'Gode', subCities: [] },
      { city: 'Jigjiga', subCities: [] },
      { city: 'Kebri Dehar', subCities: [] },
      { city: 'Kelafo', subCities: [] },
      { city: 'Shilabo', subCities: [] },
      { city: 'Warder', subCities: [] },
    ],
  },

  {
    region: 'South Ethiopia',
    cities: [
      { city: 'Arba Minch', subCities: [] },
      { city: 'Chencha', subCities: [] },
      { city: 'Dawro', subCities: [] },
      { city: 'Dimeka', subCities: [] },
      { city: 'Gofa', subCities: [] },
      { city: 'Jinka', subCities: [] },
      { city: 'Karat', subCities: [] },
      { city: 'Konso', subCities: [] },
      { city: 'Sawla', subCities: [] },
      { city: 'Wolaita Sodo', subCities: [] },
    ],
  },

  {
    region: 'South West Ethiopia',
    cities: [
      { city: 'Bonga', subCities: [] },
      { city: 'Jimma', subCities: [] },
      { city: 'Masha', subCities: [] },
      { city: 'Mizan Aman', subCities: [] },
      { city: 'Tercha', subCities: [] },
      { city: 'Tepi', subCities: [] },
    ],
  },

  {
    region: 'Tigray',
    cities: [
      { city: 'Adigrat', subCities: [] },
      { city: 'Adwa', subCities: [] },
      { city: 'Alamata', subCities: [] },
      { city: 'Axum', subCities: [] },
      { city: 'Maychew', subCities: [] },
      { city: 'Mekelle', subCities: [] },
      { city: 'Shire', subCities: [] },
      { city: 'Wukro', subCities: [] },
    ],
  },
];


function getCitiesForRegion(regionName) {
  const region = locationTree.find((row) => row.region === regionName)
  return region ? region.cities.map((row) => row.city) : []
}

function getSubCitiesForRegionCity(regionName, cityName) {
  const region = locationTree.find((row) => row.region === regionName)
  if (!region) return []
  const city = region.cities.find((row) => row.city === cityName)
  return city ? [...city.subCities] : []
}

function withCurrentOption(options, currentValue) {
  const list = Array.isArray(options) ? [...options] : []
  const value = String(currentValue || '').trim()
  if (value && !list.includes(value)) list.unshift(value)
  return list
}

function emptyForm() {
  return {
    emp_idno: '',
    full_name: '',
    mother_name: '',
    sex: '',
    birth_date: '',
    phone_number: '',
    email: '',
    region: '',
    city: '',
    subcity: '',
    woreda: '',
    kebele: '',
    house_no: '',
    photo: '',
    contact_name: '',
    contact_phone: '',
    contact_address: '',
    employment_type: '',
    basic_salary: '',
    shift_time: '',
    off_duty_day: '',
    start_date: '',
    g_date: '',
    g_name: '',
    g_region: '',
    g_city: '',
    g_subcity: '',
    g_woreda: '',
    g_kebele: '',
    g_houseno: '',
    g_phone: '',
    g_relation: '',
    g_jobtype: '',
    g_company: '',
    g_income: '',
    g_photo: '',
    g_remark: '',
  }
}

function emptyQualification() {
  return { edu_level: '', field: '', college: '', graduated_year: '' }
}

function emptyExperience() {
  return { from: '', to: '', position: '', company: '' }
}

const form = reactive(emptyForm())
const netSalary = ref('')
const qualifications = ref([emptyQualification()])
const experiences = ref([emptyExperience()])
const allowanceTypes = ref([])
const allowanceValues = reactive({})
const shiftTimes = ref([])
const formDataLoading = ref(false)
const formDataError = ref('')
const saving = ref(false)
const uploadingPhoto = ref(false)
const uploadingGuaranteePhoto = ref(false)

const showExtraSections = computed(() => {
  return !!form.employment_type && form.employment_type !== 'Commission'
})

const regionOptions = computed(() => {
  const names = locationTree.map((row) => row.region)
  return withCurrentOption(
    withCurrentOption(names, form.region),
    form.g_region
  )
})

const personalCityOptions = computed(() =>
  withCurrentOption(getCitiesForRegion(form.region), form.city)
)

const personalSubCityOptions = computed(() =>
  withCurrentOption(getSubCitiesForRegionCity(form.region, form.city), form.subcity)
)

const guaranteeCityOptions = computed(() =>
  withCurrentOption(getCitiesForRegion(form.g_region), form.g_city)
)

const guaranteeSubCityOptions = computed(() =>
  withCurrentOption(getSubCitiesForRegionCity(form.g_region, form.g_city), form.g_subcity)
)

function updateBasicSalary(event) {
  const value = event.target.value
  form.basic_salary = value
  netSalary.value = value
}

function updateNetSalary(event) {
  const value = event.target.value
  netSalary.value = value
  form.basic_salary = value
}

function onPersonalRegionChange() {
  form.city = ''
  form.subcity = ''
}

function onPersonalCityChange() {
  form.subcity = ''
}

function onGuaranteeRegionChange() {
  form.g_city = ''
  form.g_subcity = ''
}

function onGuaranteeCityChange() {
  form.g_subcity = ''
}

function clearAllowanceValues() {
  Object.keys(allowanceValues).forEach((key) => {
    delete allowanceValues[key]
  })
}

function syncAllowanceValues(types, savedAllowances = []) {
  const nextIds = new Set((types || []).map((t) => String(t.id)))
  Object.keys(allowanceValues).forEach((key) => {
    if (!nextIds.has(String(key))) delete allowanceValues[key]
  })
  const savedMap = {}
  ;(savedAllowances || []).forEach((row) => {
    if (row?.id != null) savedMap[String(row.id)] = row.amount ?? ''
  })
  ;(types || []).forEach((item) => {
    const key = item.id
    const saved = savedMap[String(key)]
    if (saved !== undefined) {
      allowanceValues[key] = saved
    } else if (allowanceValues[key] === undefined) {
      allowanceValues[key] = ''
    }
  })
}

function resetForm() {
  Object.assign(form, emptyForm())
  netSalary.value = ''
  qualifications.value = [emptyQualification()]
  experiences.value = [emptyExperience()]
  clearAllowanceValues()
}

function applyEmployeeDetail(detail) {
  if (!detail) return

  form.emp_idno = detail.id != null ? String(detail.id) : form.emp_idno
  form.full_name = detail.full_name || ''
  form.mother_name = detail.mother_name || ''
  form.sex = detail.sex || ''
  form.birth_date = detail.birth_date || ''
  form.phone_number = detail.phone_number || ''
  form.email = detail.email || ''
  form.photo = normalizeUploadValue(detail.photo || '')
  form.region = detail.region || ''
  form.city = detail.city || ''
  form.subcity = detail.subcity || ''
  form.woreda = detail.woreda || ''
  form.kebele = detail.kebele || ''
  form.house_no = detail.house_no || ''
  form.contact_name = detail.contact_name || ''
  form.contact_phone = detail.contact_phone || ''
  form.contact_address = detail.contact_address || ''
  form.employment_type = detail.employment_type || ''
  form.basic_salary =
    detail.salary !== null && detail.salary !== undefined && detail.salary !== ''
      ? detail.salary
      : detail.basic_salary != null && detail.basic_salary !== ''
        ? detail.basic_salary
        : ''
  netSalary.value = form.basic_salary
  form.shift_time = detail.shift_time != null ? String(detail.shift_time) : ''
  form.off_duty_day = detail.off_duty_day || ''
  form.start_date = detail.start_date || ''
  form.g_date = detail.g_date || ''
  form.g_name = detail.g_name || ''
  form.g_region = detail.g_region || ''
  form.g_city = detail.g_city || ''
  form.g_subcity = detail.g_subcity || ''
  form.g_woreda = detail.g_woreda || ''
  form.g_kebele = detail.g_kebele || ''
  form.g_houseno = detail.g_houseno || ''
  form.g_phone = detail.g_phone || ''
  form.g_relation = detail.g_relation || ''
  form.g_jobtype = detail.g_jobtype || ''
  form.g_company = detail.g_company || ''
  form.g_income = detail.g_income || ''
  form.g_photo = normalizeUploadValue(detail.g_photo || '')
  form.g_remark = detail.g_remark || ''

  const quals = Array.isArray(detail.qualifications) ? detail.qualifications : []
  qualifications.value = quals.length
    ? quals.map((row) => ({
        edu_level: row.edu_level || '',
        field: row.field || '',
        college: row.college || '',
        graduated_year: row.graduated_year || '',
      }))
    : [emptyQualification()]

  const exps = Array.isArray(detail.experiences) ? detail.experiences : []
  experiences.value = exps.length
    ? exps.map((row) => ({
        from: row.from || '',
        to: row.to || '',
        position: row.position || '',
        company: row.company || '',
      }))
    : [emptyExperience()]
}

function populateFromListEmployee(employee) {
  resetForm()
  if (!employee) return
  form.emp_idno = employee.id != null ? String(employee.id) : ''
  form.full_name = employee.full_name || ''
  form.sex = employee.sex || ''
  form.phone_number = employee.phone_number || ''
  form.basic_salary =
    employee.salary !== null && employee.salary !== undefined && employee.salary !== ''
      ? employee.salary
      : ''
  netSalary.value = form.basic_salary
}

async function handlePhotoUpload(event) {
  const file = event.target.files?.[0]
  if (!file) return

  if (file.size > 3 * 1024 * 1024) {
    alert('File is too large. Max 3MB.')
    event.target.value = ''
    return
  }

  uploadingPhoto.value = true
  try {
    const formData = new FormData()
    formData.append('logo', file)
    const res = await apiFetch('/upload', {
      method: 'POST',
      body: formData,
    })
    if (res.ok) {
      const data = await res.json()
      form.photo = normalizeUploadValue(data.url)
    } else {
      throw new Error('Upload failed')
    }
  } catch (err) {
    console.error('Error uploading employee photo:', err)
    alert('Failed to upload image.')
  } finally {
    uploadingPhoto.value = false
    event.target.value = ''
  }
}

async function handleGuaranteePhotoUpload(event) {
  const file = event.target.files?.[0]
  if (!file) return

  if (file.size > 3 * 1024 * 1024) {
    alert('File is too large. Max 3MB.')
    event.target.value = ''
    return
  }

  uploadingGuaranteePhoto.value = true
  try {
    const formData = new FormData()
    formData.append('logo', file)
    const res = await apiFetch('/upload', {
      method: 'POST',
      body: formData,
    })
    if (res.ok) {
      const data = await res.json()
      form.g_photo = normalizeUploadValue(data.url)
    } else {
      throw new Error('Upload failed')
    }
  } catch (err) {
    console.error('Error uploading guarantee photo:', err)
    alert('Failed to upload image.')
  } finally {
    uploadingGuaranteePhoto.value = false
    event.target.value = ''
  }
}

async function loadEditFormData() {
  formDataLoading.value = true
  formDataError.value = ''
  try {
    const employeeId = props.employee?.id != null ? String(props.employee.id) : ''
    const qs = employeeId ? `?employeeId=${encodeURIComponent(employeeId)}` : ''
    const res = await apiFetch(`/hr/employee/edit-form-data${qs}`)
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to load employee form data')
    }
    const data = await res.json().catch(() => ({}))
    const types = Array.isArray(data.allowanceTypes) ? data.allowanceTypes : []
    const shifts = Array.isArray(data.shiftTimes) ? data.shiftTimes : []
    allowanceTypes.value = types
    shiftTimes.value = shifts

    if (data.employee) {
      applyEmployeeDetail(data.employee)
      syncAllowanceValues(types, data.employee.allowances || [])
    } else {
      syncAllowanceValues(types)
    }
  } catch (err) {
    console.error('Error loading employee edit form data:', err)
    formDataError.value = err?.message || 'Failed to load employee form data'
    allowanceTypes.value = []
    shiftTimes.value = []
    clearAllowanceValues()
  } finally {
    formDataLoading.value = false
  }
}

watch(
  () => [props.isOpen, props.employee?.id],
  async ([isOpen]) => {
    if (isOpen) {
      populateFromListEmployee(props.employee)
      uploadingPhoto.value = false
      uploadingGuaranteePhoto.value = false
      await loadEditFormData()
    } else {
      allowanceTypes.value = []
      shiftTimes.value = []
      clearAllowanceValues()
      formDataError.value = ''
      saving.value = false
      uploadingPhoto.value = false
      uploadingGuaranteePhoto.value = false
    }
  },
  { immediate: true }
)

function addQualification() {
  qualifications.value.push(emptyQualification())
}

function removeQualification(idx) {
  qualifications.value.splice(idx, 1)
  if (qualifications.value.length === 0) {
    qualifications.value.push(emptyQualification())
  }
}

function addExperience() {
  experiences.value.push(emptyExperience())
}

function removeExperience(idx) {
  experiences.value.splice(idx, 1)
  if (experiences.value.length === 0) {
    experiences.value.push(emptyExperience())
  }
}

function closeModal() {
  uploadingPhoto.value = false
  uploadingGuaranteePhoto.value = false
  emit('close')
}

async function handleSubmit() {
  if (saving.value || formDataLoading.value || uploadingPhoto.value || uploadingGuaranteePhoto.value) return
  const employeeId = toIntOrNull(form.emp_idno) || toIntOrNull(props.employee?.id)
  if (!employeeId) {
    formDataError.value = 'Employee ID is required.'
    return
  }

  saving.value = true
  formDataError.value = ''
  try {
    const allowances = allowanceTypes.value.map((item) => ({
      id: item.id,
      description: item.description,
      amount: allowanceValues[item.id] ?? '',
    }))

    const payload = {
      full_name: form.full_name,
      mother_name: form.mother_name,
      sex: form.sex,
      birth_date: form.birth_date,
      phone_number: form.phone_number,
      email: form.email,
      photo: normalizeUploadValue(form.photo) || null,
      region: form.region,
      city: form.city,
      subcity: form.subcity,
      woreda: form.woreda,
      kebele: form.kebele,
      house_no: form.house_no,
      contact_name: form.contact_name,
      contact_phone: form.contact_phone,
      contact_address: form.contact_address,
      employment_type: form.employment_type,
      basic_salary: form.basic_salary,
      shift_time: form.shift_time,
      off_duty_day: form.off_duty_day,
      start_date: form.start_date,
      allowances,
      qualifications: qualifications.value.map((row) => ({ ...row })),
      experiences: experiences.value.map((row) => ({ ...row })),
      g_date: form.g_date,
      g_name: form.g_name,
      g_region: form.g_region,
      g_city: form.g_city,
      g_subcity: form.g_subcity,
      g_woreda: form.g_woreda,
      g_kebele: form.g_kebele,
      g_houseno: form.g_houseno,
      g_phone: form.g_phone,
      g_relation: form.g_relation,
      g_jobtype: form.g_jobtype,
      g_company: form.g_company,
      g_income: form.g_income,
      g_photo: normalizeUploadValue(form.g_photo) || null,
      g_remark: form.g_remark,
    }

    const res = await apiFetch(`/hr/employee/${employeeId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to save employee details')
    }
    const data = await res.json().catch(() => ({}))
    emit('save', data.employee || payload)
    emit('close')
  } catch (err) {
    console.error('Error saving employee details:', err)
    formDataError.value = err?.message || 'Failed to save employee details'
  } finally {
    saving.value = false
  }
}

function toIntOrNull(v) {
  if (v === undefined || v === null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? parseInt(String(n), 10) : null
}
</script>
